/**
 * Navigation Bar Component
 *
 * A responsive navigation header that adapts to different screen sizes and user authentication states.
 * This component provides the main navigation interface for the application, including:
 *
 * - Responsive design with desktop and mobile layouts
 * - User authentication state awareness (logged in vs. logged out)
 * - User profile menu with avatar and dropdown options
 * - Language switching functionality
 * - Mobile-friendly navigation drawer
 *
 * The component handles different states:
 * - Loading state with skeleton placeholders
 * - Authenticated state with user profile information
 * - Unauthenticated state with sign in/sign up buttons
 */
import { CogIcon, HomeIcon, LogOutIcon, MenuIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import LangSwitcher from "./lang-switcher";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Separator } from "./ui/separator";
import {
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./ui/sheet";
import { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuTrigger, navigationMenuTriggerStyle, NavigationMenuContent, NavigationMenuLink } from "./ui/navigation-menu";

type MenuItem = {
  labelKey: string;
  to: string;
  descriptionKey: string;
};

type Menu = {
  labelKey: string;
  to: string;
  items?: MenuItem[];
};

const menus: Menu[] = [
  {
    labelKey: "navigation.about",
    to: "/about",
    items: [
      {
        labelKey: "navigation.gym",
        descriptionKey: "navigation.gymDescription",
        to: "/about/gym",
      },
      {
        labelKey: "navigation.coach",
        descriptionKey: "navigation.coachDescription",
        to: "/about/coach",
      },
      {
        labelKey: "navigation.facilities",
        descriptionKey: "navigation.facilitiesDescription",
        to: "/about/facilities",
      },
    ],
  },
  {
    labelKey: "navigation.programs",
    to: "/programs",
    items: [
      {
        labelKey: "navigation.info",
        descriptionKey: "navigation.infoDescription",
        to: "/programs/info",
      },
      {
        labelKey: "navigation.schedule",
        descriptionKey: "navigation.scheduleDescription",
        to: "/programs/schedule",
      },
    ],
  },
  {
    labelKey: "navigation.community",
    to: "/community",
    items: [
      {
        labelKey: "navigation.announcements",
        descriptionKey: "navigation.announcementsDescription",
        to: "/community/announcements",
      },
      {
        labelKey: "navigation.news",
        descriptionKey: "navigation.newsDescription",
        to: "/community/news",
      },
    ],
  },
  {
    labelKey: "navigation.etc",
    to: "/etc",
    items: [
      {
        labelKey: "navigation.faq",
        descriptionKey: "navigation.faqDescription",
        to: "/etc/faq",
      },
      {
        labelKey: "navigation.contact",
        descriptionKey: "navigation.contactDescription",
        to: "/etc/contact",
      },
    ],
  },
];

/**
 * UserMenu Component
 * 
 * Displays the authenticated user's profile menu with avatar and dropdown options.
 * This component is shown in the navigation bar when a user is logged in and provides
 * quick access to user-specific actions and information.
 * 
 * Features:
 * - Avatar display with image or fallback initials
 * - User name and email display
 * - Quick navigation to dashboard
 * - Logout functionality
 * 
 * @param name - The user's display name
 * @param email - The user's email address (optional)
 * @param avatarUrl - URL to the user's avatar image (optional)
 * @returns A dropdown menu component with user information and actions
 */
function UserMenu({
  name,
  email,
  avatarUrl,
  closeOnSelect = false,
}: {
  name: string;
  email?: string;
  avatarUrl?: string | null;
  closeOnSelect?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <DropdownMenu>
      {/* Avatar as the dropdown trigger */}
      <DropdownMenuTrigger asChild>
        <Avatar className="size-8 cursor-pointer rounded-lg">
          <AvatarImage src={avatarUrl ?? undefined} />
          <AvatarFallback>{name.slice(0, 2)}</AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      
      {/* Dropdown content with user info and actions */}
      <DropdownMenuContent className="w-56">
        {/* User information display */}
        <DropdownMenuLabel className="grid flex-1 text-left text-sm leading-tight">
          <span className="truncate font-semibold">{name}</span>
          <span className="truncate text-xs">{email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {closeOnSelect ? (
          <>
            <DropdownMenuItem asChild>
              <SheetClose asChild>
                <Link to="/dashboard" viewTransition prefetch="intent">
                  <HomeIcon className="size-4" />
                  {t("navigation.dashboard")}
                </Link>
              </SheetClose>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <SheetClose asChild>
                <Link to="/logout" viewTransition prefetch="intent">
                  <LogOutIcon className="size-4" />
                  {t("navigation.logout")}
                </Link>
              </SheetClose>
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuItem asChild>
              <Link to="/dashboard" viewTransition prefetch="intent">
                <HomeIcon className="size-4" />
                {t("navigation.dashboard")}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/logout" viewTransition prefetch="intent">
                <LogOutIcon className="size-4" />
                {t("navigation.logout")}
              </Link>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * AuthButtons Component
 * 
 * Displays authentication buttons (Sign in and Sign up) for unauthenticated users.
 * This component is shown in the navigation bar when no user is logged in and provides
 * quick access to authentication screens.
 * 
 * Features:
 * - Sign in button with ghost styling (less prominent)
 * - Sign up button with default styling (more prominent)
 * - View transitions for smooth navigation to auth screens
 * - Compatible with mobile navigation drawer (SheetClose integration)
 * 
 * @returns Fragment containing sign in and sign up buttons
 */
function AuthButtons() {
  const { t } = useTranslation();
  return (
    <>
      {/* Sign in button (less prominent) */}
      <Button variant="ghost" asChild>
        <SheetClose asChild>
          <Link to="/login" viewTransition prefetch="intent">
            {t("navigation.signIn")}
          </Link>
        </SheetClose>
      </Button>
      
      {/* Sign up button (more prominent) */}
      <Button variant="default" asChild>
        <SheetClose asChild>
          <Link to="/join" viewTransition prefetch="intent">
            {t("navigation.signUp")}
          </Link>
        </SheetClose>
      </Button>
    </>
  );
}

/**
 * Actions Component
 * 
 * Displays utility actions and settings in the navigation bar, including:
 * - Debug/settings dropdown menu with links to monitoring tools
 * - Language switcher for changing the application language
 * 
 * This component is shown in the navigation bar for all users regardless of
 * authentication state and provides access to application-wide settings and tools.
 * 
 * @returns Fragment containing settings dropdown and language switcher
 */
function SettingsMenu() {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild className="cursor-pointer">
        <Button variant="ghost" size="icon">
          <CogIcon className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {/* Sentry monitoring link */}
        <DropdownMenuItem asChild>
          <SheetClose asChild>
            <Link to="/debug/sentry" viewTransition prefetch="intent">
              Sentry
            </Link>
          </SheetClose>
        </DropdownMenuItem>
        {/* Google Analytics link */}
        <DropdownMenuItem asChild>
          <SheetClose asChild>
            <Link to="/debug/analytics" viewTransition prefetch="intent">
              Google Tag
            </Link>
          </SheetClose>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Actions({ showSettings }: { showSettings: boolean }) {
  return (
    <>
      {showSettings ? <SettingsMenu /> : null}
      {/* Language switcher component */}
      <LangSwitcher />
    </>
  );
}

/**
 * NavigationBar Component
 * 
 * The main navigation header for the application that adapts to different screen sizes
 * and user authentication states. This component serves as the primary navigation
 * interface and combines several sub-components to create a complete navigation experience.
 * 
 * Features:
 * - Responsive design with desktop navigation and mobile drawer
 * - Application branding with localized title
 * - Main navigation links
 * - User authentication state handling (loading, authenticated, unauthenticated)
 * - User profile menu with avatar for authenticated users
 * - Sign in/sign up buttons for unauthenticated users
 * - Theme and language switching options
 * 
 * @param name - The authenticated user's name (if available)
 * @param email - The authenticated user's email (if available)
 * @param avatarUrl - The authenticated user's avatar URL (if available)
 * @param loading - Boolean indicating if the auth state is still loading
 * @returns The complete navigation bar component
 */
export function NavigationBar({
  name,
  email,
  avatarUrl,
  loading,
  isAdmin = false,
}: {
  name?: string;
  email?: string;
  avatarUrl?: string | null;
  loading: boolean;
  isAdmin?: boolean;
}) {
  // Get translation function for internationalization
  const { t } = useTranslation();
  
  return (
    <nav
      className={
        "relative z-50 mx-auto flex h-16 w-full items-center justify-between border-b px-5 shadow-xs backdrop-blur-lg transition-opacity md:px-10"
      }
    >
      <div className="mx-auto grid h-full w-full max-w-screen-2xl grid-cols-[1fr_auto] items-center py-3 md:grid-cols-[1fr_auto_1fr]">
        {/* Application logo/title with link to home */}
        <div className="justify-self-start">
          <Link to="/" prefetch="intent">
            <h1 className="text-lg font-extrabold">{t("home.title")}</h1>
          </Link>
        </div>
        
        {/* Desktop navigation menu (hidden on mobile) */}
        <div className="hidden items-center md:flex justify-self-center">
          {/* Main navigation links */}
          <NavigationMenu>
            <NavigationMenuList>
              {menus.map((menu) => (
                <NavigationMenuItem key={t(menu.labelKey)}>
                  {menu.items ? (
                    <>
                    <NavigationMenuTrigger>{t(menu.labelKey)}</NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <ul className="grid w-[600px] font-light gap-3 p-4 grid-cols-2">
                        {menu.items?.map((item) => (
                          <NavigationMenuItem key={t(item.labelKey)} className="select-none rounded-md transition-colors focus:bg-accent  hover:bg-accent">
                            <NavigationMenuLink asChild>
                              <Link
                                className="p-3 space-y-1 block leading-none no-underline outline-none"
                                to={item.to}
                                prefetch="intent"
                              >
                                  <span className="text-sm font-medium leading-none">{t(item.labelKey)}</span>
                                  <p className="text-sm leading-snug text-muted-foreground">{t(item.descriptionKey)}</p>
                              </Link>
                            </NavigationMenuLink>
                          </NavigationMenuItem>
                        ))}
                        </ul>
                        </NavigationMenuContent>
                        </>
                  ) : (
                    <Link
                      className={navigationMenuTriggerStyle()}
                      to={menu.to}
                      prefetch="intent"
                    >
                      {t(menu.labelKey)}
                    </Link>
                  )}
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
            </NavigationMenu>
            </div>
          
          <div className="hidden items-center gap-4 justify-self-end md:flex">
          {/* Settings and language switcher */}
          <Actions showSettings={isAdmin} />
          
          <div
            aria-hidden="true"
            className="bg-foreground/25 mx-0.5 h-5 w-px shrink-0"
          />
          
          {/* Conditional rendering based on authentication state */}
          {loading ? (
            // Loading state with skeleton placeholder
            <div className="flex items-center">
              <div className="bg-muted-foreground/20 size-8 animate-pulse rounded-lg" />
            </div>
          ) : (
            <>
              {name ? (
                // Authenticated state with user menu
                <UserMenu name={name} email={email} avatarUrl={avatarUrl} />
              ) : (
                // Unauthenticated state with auth buttons
                <AuthButtons />
              )}
            </>
          )}
          </div>
        
        {/* Mobile header actions (hidden on desktop) */}
        <div className="flex items-center gap-2 justify-self-end md:hidden">
          <LangSwitcher />
          {!loading && name ? (
            <UserMenu name={name} email={email} avatarUrl={avatarUrl} />
          ) : null}
          <SheetTrigger className="-mr-2 rounded-md p-2">
            <MenuIcon />
          </SheetTrigger>
        </div>
        <SheetContent className="overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{t("home.title")}</SheetTitle>
          </SheetHeader>
          {!loading && !name ? (
            <div className="grid grid-cols-2 gap-2 px-4">
              <AuthButtons />
            </div>
          ) : null}
          <div className="flex flex-col gap-6 px-4">
            {menus.map((menu) => (
              <div key={menu.labelKey} className="flex flex-col gap-2">
                <div className="text-muted-foreground text-xs font-semibold tracking-widest uppercase">
                  {t(menu.labelKey)}
                </div>
                <div className="flex flex-col gap-1">
                  {menu.items?.map((item) => (
                    <SheetClose key={item.to} asChild>
                      <Link
                        to={item.to}
                        className="hover:bg-accent rounded-md px-3 py-2 text-sm font-medium transition-colors"
                        viewTransition
                        prefetch="intent"
                      >
                        {t(item.labelKey)}
                      </Link>
                    </SheetClose>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {loading ? (
            <div className="flex items-center">
              <div className="bg-muted-foreground h-4 w-24 animate-pulse rounded-full" />
            </div>
          ) : (
            <SheetFooter>
              {name ? (
                <div className="flex w-full justify-start">
                  {isAdmin ? <SettingsMenu /> : null}
                </div>
              ) : (
                <div className="flex flex-col gap-5" />
              )}
            </SheetFooter>
          )}
        </SheetContent>
      </div>
    </nav>
  );
}
